import { NetworkStatus, useApolloClient, useMutation, useQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import driverPlaceholder from 'assets/images/driver-placeholder.svg'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import Dropdown from 'components/v2/Dropdown'
import EmptyState from 'components/v2/EmptyState'
import { driverRSVPMessages } from 'features/Trips/components/DriverDetails/messages'
import { GET_TRIP_DRIVER } from 'features/Trips/queries'
import { GTTripV2Driver, GTTripV2DriverRsvpStatus, GTTripV2Note } from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'
import { getShortId } from 'utils/string'
import { TDriverDocumentCategory, TripV2DriverRsvpStatus } from 'gql/generated/graphql'
import { CloseIcon, HandshakeIcon } from 'components/Icons'
import { Input } from 'components/Input'
import { ADD_TRIP_DRIVER_RSVP_NOTE } from 'features/Trips/mutations'
import { updateManualRsvpStatus } from 'features/Trips/cache'
import { ErrorMessage } from 'components/v2/ErrorMessage'

const colors = {
  [GTTripV2DriverRsvpStatus.Confirmed]: '#00C875',
  [GTTripV2DriverRsvpStatus.Declined]: '#FF627B',
  [GTTripV2DriverRsvpStatus.Failed]: '#FF627B',
  [GTTripV2DriverRsvpStatus.Missed]: '#FFA800',
  [GTTripV2DriverRsvpStatus.Sent]: '#8F8BFF'
}

const DriverInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DriverName = styled.div`
  color: #5d59d7;
  cursor: pointer;
  font-size: 14px;
  line-height: 16.41px;
  text-decoration: underline;
`

const DriverRSVP = styled.div<{ status: GTTripV2DriverRsvpStatus }>`
  white-space: nowrap;
  color: ${({ status }): string => colors[status]};
  font-size: 14px;
  font-weight: 500;
  line-height: 16.41px;
`

interface DriverCellProps {
  tripId: string
  driver?: GTTripV2Driver
  rsvpNote?: GTTripV2Note | null
}

export const DriverCell = ({ driver, tripId, rsvpNote }: DriverCellProps): ReactElement => {
  const intl = useIntl()
  const [isOpenDropdown, setOpenDropdown] = useState(false)

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  if (driver === undefined) return <>-</>

  const status = driver.RSVP?.status
  const attempts = driver.RSVP?.attempt ?? 0
  let rsvpMessage = safeFormatMessage({ intl, messages: driverRSVPMessages, key: status ?? '' })
  if (attempts > 0) {
    rsvpMessage = `${rsvpMessage} (${attempts})`
  }

  return (
    <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setOpenDropdown(false)}>
      <DriverInfo>
        <DriverName onClick={toggleDropdown}>{`${driver.firstName} ${driver.lastName}`}</DriverName>
        {status && <DriverRSVP status={status}>A: {rsvpMessage}</DriverRSVP>}
        {rsvpNote && (
          <DriverRSVP status={rsvpNote.driver?.rsvpStatus as GTTripV2DriverRsvpStatus}>
            M: {safeFormatMessage({ intl, messages: driverRSVPMessages, key: rsvpNote.driver?.rsvpStatus ?? '' })}
          </DriverRSVP>
        )}
      </DriverInfo>
      <Dropdown.Content open={isOpenDropdown} noIndicator right noOverflow>
        <DriverDetails tripId={tripId} driverId={driver.id} onClose={(): void => setOpenDropdown(false)} />
      </Dropdown.Content>
    </Dropdown.Wrapper>
  )
}

const DriverDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 16px;
  padding: 24px;
`

const DriverDetailsHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const DriverDetailsTitle = styled.div`
  font-size: 24px;
  font-weight: 500;
`

const CloseWrapper = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const DriverDetailsPhoto = styled.img`
  border-radius: 24px;
  height: 130px;
  width: 130px;
  margin: 0 auto;
`

const DriverDetailsName = styled.div`
  font-size: 22px;
  font-weight: 500;
  line-height: 25.78px;
  text-align: center;
`

const DriverDetailsID = styled.div`
  color: #b0b0b0;
  font-size: 18px;
  line-height: 21.09px;
  text-align: center;
`

const DriverDetailsPartnerWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 8px;
`

const StyledPartnerIcon = styled(HandshakeIcon)`
  fill: #916ee8;
  height: 20px;
  width: 20px;
`

const DriverDetailsPartner = styled.div`
  font-size: 14px;
  line-height: 16.41px;
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  padding: 0 8px;
`

interface DriverDetailsProps {
  tripId: string
  driverId: string
  onClose: () => void
}

const DriverDetails = ({ tripId, driverId, onClose }: DriverDetailsProps): ReactElement => {
  const client = useApolloClient()
  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TRIP_DRIVER, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: driverId
      }
    }
  })
  const [declineAssistanceLoading, setDeclineAssistanceLoading] = useState(false)
  const [confirmAssistanceLoading, setConfirmAssistanceLoading] = useState(false)
  const [addTripDriverRSVPNote, { error: driverRSVPNoteError, loading: driverRSVPNoteLoading }] =
    useMutation(ADD_TRIP_DRIVER_RSVP_NOTE)

  const declineAssistance = async (): Promise<void> => {
    try {
      setDeclineAssistanceLoading(true)
      await addTripDriverRSVPNote({
        variables: {
          input: {
            id: tripId,
            driverID: driverId,
            status: TripV2DriverRsvpStatus.Declined
          }
        }
      })
      updateManualRsvpStatus({
        client,
        tripId,
        driver: {
          id: driverId,
          firstName: data?.tdriver?.userAccount?.firstName || '',
          lastName: data?.tdriver?.userAccount?.lastName || ''
        },
        driverRSVPStatus: TripV2DriverRsvpStatus.Declined
      })
      onClose()
    } catch (error) {
      // Do nothing
    }
    setDeclineAssistanceLoading(false)
  }
  const confirmAssistance = async (): Promise<void> => {
    try {
      setConfirmAssistanceLoading(true)
      await addTripDriverRSVPNote({
        variables: {
          input: {
            id: tripId,
            driverID: driverId,
            status: TripV2DriverRsvpStatus.Confirmed
          }
        }
      })
      updateManualRsvpStatus({
        client,
        tripId,
        driver: {
          id: driverId,
          firstName: data?.tdriver?.userAccount?.firstName || '',
          lastName: data?.tdriver?.userAccount?.lastName || ''
        },
        driverRSVPStatus: TripV2DriverRsvpStatus.Confirmed
      })
      onClose()
    } catch (error) {
      // Do nothing
    }
    setConfirmAssistanceLoading(false)
  }
  let content = <></>
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trips.driver.details.description"
            defaultMessage="There was an error while loading your driver"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="trips.driver.details.error.action" defaultMessage="Reload your driver" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    const driver = data?.tdriver
    const driverId = driver?.id ?? ''
    const fullName = `${driver?.userAccount?.firstName ?? ''} ${driver?.userAccount?.lastName ?? ''}`
    const partnerName = driver?.partner?.name ?? ''
    const driverDocuments = driver?.documents ?? []
    const driverPhoto = driverDocuments.find(({ category }) => category === TDriverDocumentCategory.Photo)
    const photoPath = driverPhoto?.document.path ?? driverPlaceholder

    content = (
      <>
        <DriverDetailsPhoto src={photoPath} alt={fullName} />
        <div>
          <DriverDetailsName>{fullName}</DriverDetailsName>
          <DriverDetailsID>
            <FormattedMessage
              id="trips.driver.details.id"
              defaultMessage="ID: {id}"
              values={{ id: getShortId(driverId).toUpperCase() }}
            />
          </DriverDetailsID>
        </div>
        <DriverDetailsPartnerWrapper>
          <StyledPartnerIcon />
          <DriverDetailsPartner>{partnerName}</DriverDetailsPartner>
        </DriverDetailsPartnerWrapper>
        <Input
          id="phone"
          value={driver?.userAccount?.phoneNumber?.number ?? ''}
          label={<FormattedMessage id="trips.driver.details.phone" defaultMessage="Phone" />}
          onChange={(): void => undefined}
        />
        <Input
          id="email"
          value={driver?.userAccount?.email ?? ''}
          label={<FormattedMessage id="trips.driver.details.email" defaultMessage="Email" />}
          onChange={(): void => undefined}
        />
        {driverRSVPNoteError && (
          <ErrorMessage>
            <FormattedMessage
              id="trips.driver.details.error"
              defaultMessage="There was an error while updating the driver RSVP status"
            />
          </ErrorMessage>
        )}
        <ButtonsWrapper>
          <Button
            type="button"
            kind="cancel"
            small
            onClick={declineAssistance}
            disabled={driverRSVPNoteLoading}
            loading={declineAssistanceLoading}
          >
            <FormattedMessage id="trips.driver.details.decline" defaultMessage="Decline" />
          </Button>
          <Button
            type="button"
            kind="primary"
            small
            onClick={confirmAssistance}
            disabled={driverRSVPNoteLoading}
            loading={confirmAssistanceLoading}
          >
            <FormattedMessage id="trips.driver.details.confirm" defaultMessage="Confirm" />
          </Button>
        </ButtonsWrapper>
      </>
    )
  }

  return (
    <DriverDetailsWrapper>
      <DriverDetailsHeader>
        <DriverDetailsTitle>
          <FormattedMessage id="trips.driver.details.title" defaultMessage="Driver Details" />
        </DriverDetailsTitle>
        <CloseWrapper onClick={(): void => onClose?.()}>
          <CloseIcon />
        </CloseWrapper>
      </DriverDetailsHeader>
      {content}
    </DriverDetailsWrapper>
  )
}
